import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import api from '../../services/api';

/**
 * Admin → Call Audio Retention (23 Sep 2026).
 *
 * Whether the transcription worker keeps each speaker's live-call audio, and
 * for how long, so transcription and summaries can be re-run while their
 * quality is worked on. Every change here, and every recording kept, is
 * written to the activity log.
 */

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

const CallAudioRetention = () => {
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState('30');
  const [limits, setLimits] = useState({ minDays: 1, maxDays: 365 });
  const [lastModifiedAt, setLastModifiedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const apply = (settings) => {
    setEnabled(settings.enabled);
    setDays(String(settings.retentionDays));
    setLastModifiedAt(settings.lastModifiedAt || null);
  };

  useEffect(() => {
    (async () => {
      try {
        const data = unwrap(await api.get('/admin/call-audio-retention/settings'));
        apply(data.settings);
        if (data.limits) setLimits(data.limits);
      } catch (err) {
        setError(errorText(err, 'Could not load the call audio retention setting.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = unwrap(await api.put('/admin/call-audio-retention/settings', { enabled, retentionDays: Number(days) }));
      apply(data.settings);
      setSuccess(data.settings.enabled
        ? `Saved. Call audio is kept for ${data.settings.retentionDays} days.`
        : 'Saved. Call audio is no longer kept.');
    } catch (err) {
      setError(errorText(err, 'Could not save the setting.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  const daysNumber = Number(days);
  const daysValid = Number.isInteger(daysNumber) && daysNumber >= limits.minDays && daysNumber <= limits.maxDays;

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Typography variant="h5" gutterBottom>Call Audio Retention</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Keep each speaker's audio from live video calls so transcription and call summaries can be re-run
        and compared while their quality is being improved.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Consent point 7 tells clients that video appointments are kept as written transcripts only.
        Turn this off before real clients have calls.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Switch id="call-audio-retention-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label={enabled ? 'Keeping call audio' : 'Not keeping call audio'}
          />
          <TextField
            id="call-audio-retention-days"
            label="Keep each recording for (days)"
            type="number"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            disabled={!enabled}
            error={enabled && !daysValid}
            helperText={enabled && !daysValid
              ? `A whole number from ${limits.minDays} to ${limits.maxDays}.`
              : 'Each recording is deleted automatically once this many days have passed, usually within a day of that date. A change applies to recordings made after it; existing recordings keep their date.'}
            inputProps={{ min: limits.minDays, max: limits.maxDays, step: 1 }}
            sx={{ maxWidth: 320 }}
          />
          <Typography variant="body2" color="text.secondary">
            Every change to this setting, and every recording kept, is recorded in the Activity Log.
            {lastModifiedAt ? ` Last changed ${new Date(lastModifiedAt).toLocaleString()}.` : ''}
          </Typography>
          <Box>
            <Button variant="contained" onClick={save} disabled={saving || (enabled && !daysValid)}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CallAudioRetention;
